import { Box } from '@mui/material';
import { Typography } from 'src/components/new/Typography';
import LockedInput from '../pricing-inputs/LockedInput';
import PricingTable from 'src/components/table/PricingTable';
import { useSelector } from 'react-redux';
import { RootState } from 'src/store/store';
import { useMemo } from 'react';
import { mapPostageType } from './FirstClassPostageRates';

const RATE_FIELDS = ['certified_canada', 'certified_mexico', 'certified_international'] as const;

const WEIGHT_CONFIG: Record<string, { weight: string; range: React.ReactNode }> = {
  '16': {
    weight: '1 - 21',
    range: (
      <>
        1 → <b>120</b>
      </>
    ),
  },
  '22': {
    weight: '21 - 24',
    range: (
      <>
        121 → <b>140</b>
      </>
    ),
  },
  '92': {
    weight: '92',
    range: (
      <>
        141 → <b>500</b>
      </>
    ),
  },
};

interface RateCell {
  id: number;
  price: number;
}

interface Row {
  weight: string;
  packageType: string;
  sheetRange: React.ReactNode;
  [key: string]: any;
}

const CertifiedInternationalRates: React.FC = () => {
  const postageRates = useSelector((state: RootState) => state.admin.sortedPostageRatesV2);

  const data: Row[] = useMemo(() => {
    return Object.entries(postageRates.certified ?? {})
      .filter(([weight]) => WEIGHT_CONFIG[weight])
      .map(([weight, rates]: any) => {
        const config = WEIGHT_CONFIG[weight];

        const row: Row = {
          weight: config.weight,
          packageType: rates[0].packageType,
          sheetRange: config.range,
          hasBottomBorder: true,
        };

        rates.forEach((r: any) => {
          row[r.mailingType] = { id: r.id, price: r.price };
        });

        return row;
      });
  }, [postageRates]);

  const renderRateCell = (cell: RateCell) => <LockedInput value={`$${(cell?.price ?? 0).toFixed(2)}`} />;

  const renderCell = (row: Row, column: any) => {
    if (RATE_FIELDS.includes(column.field)) {
      const cell: RateCell = row[column.field];
      return renderRateCell(cell);
    }

    if (column.field === 'packageType') {
      return (
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Box
            sx={{
              p: 1,
              borderRadius: '999px',
              backgroundColor: '#f0f6ff',
              maxWidth: '85%',
            }}
          >
            <Typography fontWeight={500} fontSize={14}>
              {mapPostageType[row.packageType as keyof typeof mapPostageType]}
            </Typography>
          </Box>
        </Box>
      );
    }

    return row[column.field];
  };

  return (
    <PricingTable mainHeaderName="Certified" columns={internationalTableColumns} data={data} renderCell={renderCell} />
  );
};

export default CertifiedInternationalRates;

const internationalTableColumns = [
  {
    headerName: (
      <>
        <p style={{ margin: 0 }}>Weight</p>
        <p style={{ margin: 0, color: '#7e828b' }}>Not Over (oz)</p>
      </>
    ),
    field: 'weight',
    width: 70,
  },
  { headerName: 'Sheet Range', field: 'sheetRange', width: 50 },
  {
    headerName: 'Package',
    field: 'packageType',
    width: 120,
    customHeaderStyle: { borderRight: '1px solid rgba(224,224,224,1)' },
    customCellStyle: { borderRight: '1px solid rgba(224,224,224,1)' },
  },
  ...RATE_FIELDS.map((field) => ({
    headerName:
      field.replace('certified_', '').replace('_', ' ').charAt(0).toUpperCase() +
      field.replace('certified_', '').replace('_', ' ').slice(1),
    field,
    width: 120,
    customHeaderStyle: { textAlign: 'center' as const },
  })),
];